import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.refs.quantityInput.addEventListener('input', this.#onQuantityInputEdit);
    this.refs.quantityInput.addEventListener('change', this.#onQuantityInputEdit);

    this.#maxMinObserver = new MutationObserver(() => {
      this.#syncButtonDisabledState();
    });
    this.#maxMinObserver.observe(this.refs.quantityInput, {
      attributes: true,
      attributeFilter: ['min', 'max', 'disabled', 'value'],
    });

    this.#syncButtonDisabledState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.quantityInput.removeEventListener('input', this.#onQuantityInputEdit);
    this.refs.quantityInput.removeEventListener('change', this.#onQuantityInputEdit);
    this.#maxMinObserver?.disconnect();
    this.#maxMinObserver = null;
  }

  #maxMinObserver = null;

  #onQuantityInputEdit = () => {
    this.#onQuantityChange();
  };

  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    if (this.#isAtMax()) {
      this.dispatchEvent(new CustomEvent('quantity-selector:max', { bubbles: true }));
      this.#syncButtonDisabledState();
      return;
    }

    if (this.refs.quantityInput.disabled) return;
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
    if (this.#isAtMax()) {
      this.dispatchEvent(new CustomEvent('quantity-selector:max', { bubbles: true }));
    }
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (this.#isAtMin()) {
      this.#syncButtonDisabledState();
      return;
    }
    if (this.refs.quantityInput.disabled) return;
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (this.refs.quantityInput.disabled) return;
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.#syncButtonDisabledState();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const numericValue = parseInt(quantityInput.value, 10);
    const minValue = parseInt(quantityInput.min, 10);
    const maxValue = parseInt(quantityInput.max, 10);

    if (Number.isFinite(minValue) && numericValue < minValue) {
      quantityInput.value = String(minValue);
    }

    if (Number.isFinite(maxValue) && numericValue > maxValue) {
      quantityInput.value = String(maxValue);
    }
  };

  #isAtMin() {
    const { quantityInput } = this.refs;
    const numericValue = parseInt(quantityInput.value, 10);
    const minValue = parseInt(quantityInput.min, 10);
    const effectiveMin = Number.isFinite(minValue) ? minValue : 1;
    return Number.isFinite(numericValue) ? numericValue <= effectiveMin : true;
  }

  #isAtMax() {
    const { quantityInput } = this.refs;
    const numericValue = parseInt(quantityInput.value, 10);
    const maxValue = parseInt(quantityInput.max, 10);
    if (!Number.isFinite(maxValue)) return false;
    return Number.isFinite(numericValue) ? numericValue >= maxValue : false;
  }

  #syncButtonDisabledState() {
    const { quantityInput } = this.refs;
    const buttons = this.refs.quantityButtons || [];
    const isInputDisabled = quantityInput.disabled;

    let minus = null;
    let plus = null;

    for (const el of buttons) {
      if (!(el instanceof HTMLButtonElement)) continue;
      if (el.name === 'minus') minus = el;
      if (el.name === 'plus') plus = el;
    }

    if (!minus) minus = this.querySelector('button[name="minus"]');
    if (!plus) plus = this.querySelector('button[name="plus"]');

    if (minus) {
      minus.disabled = isInputDisabled || this.#isAtMin();
    }
    if (plus) {
      plus.disabled = isInputDisabled || this.#isAtMax();
    }
  }

  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
